import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Send, MessageCircle, X, Users, Circle, Minimize2, Maximize2 } from "lucide-react";

const ROLE_COLORS = {
  admin: "bg-red-500/10 text-red-600 dark:text-red-400",
  sub_admin: "bg-purple-500/10 text-purple-600 dark:text-purple-400",
  team_lead: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
  event_host: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
  vendor: "bg-teal-500/10 text-teal-600 dark:text-teal-400",
};

const ROLE_LABELS = {
  admin: "Admin",
  sub_admin: "Sub Admin",
  team_lead: "Team Lead",
  event_host: "Event Host",
  vendor: "Vendor",
};

export default function ChatPanel({ eventId }) {
  const { user, token, authAxios } = useAuth();
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [typingUsers, setTypingUsers] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  // Load chat history
  const loadHistory = useCallback(async () => {
    try {
      const api = authAxios();
      const res = await api.get(`/events/${eventId}/chat`);
      setMessages(res.data);
    } catch (err) {
      console.error("Failed to load chat history:", err);
    }
  }, [eventId, authAxios]);

  // WebSocket connection
  const connectWs = useCallback(() => {
    if (!eventId || !token) return;
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const wsUrl = backendUrl.replace(/^https?/, (m) => m === "https" ? "wss" : "ws");
    const ws = new WebSocket(`${wsUrl}/api/ws/chat/${eventId}?token=${token}`);

    ws.onopen = () => {
      console.log("Chat WebSocket connected");
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === "message") {
          setMessages((prev) => [...prev, data]);
          if (!isOpen || isMinimized) {
            setUnreadCount((prev) => prev + 1);
          }
          setTimeout(scrollToBottom, 100);
        } else if (data.type === "online_users") {
          setOnlineUsers(data.users || []);
        } else if (data.type === "user_joined" || data.type === "user_left") {
          // Refresh online users by requesting them
          setOnlineUsers((prev) => {
            if (data.type === "user_joined") {
              const exists = prev.find((u) => u.user_id === data.user_id);
              if (!exists) return [...prev, { user_id: data.user_id, name: data.user_name, role: data.user_role }];
              return prev;
            } else {
              return prev.filter((u) => u.user_id !== data.user_id);
            }
          });
        } else if (data.type === "typing") {
          if (data.sender_id !== user?.user_id) {
            setTypingUsers((prev) => {
              const exists = prev.find((u) => u.sender_id === data.sender_id);
              if (!exists) return [...prev, data];
              return prev;
            });
            // Remove typing indicator after 3s
            setTimeout(() => {
              setTypingUsers((prev) => prev.filter((u) => u.sender_id !== data.sender_id));
            }, 3000);
          }
        }
      } catch (err) {
        console.error("Chat message parse error:", err);
      }
    };

    ws.onclose = () => {
      console.log("Chat WebSocket disconnected");
      // Reconnect after 3 seconds
      reconnectTimeoutRef.current = setTimeout(() => {
        if (wsRef.current === ws) connectWs();
      }, 3000);
    };

    ws.onerror = (err) => {
      console.error("Chat WebSocket error:", err);
    };

    wsRef.current = ws;
  }, [eventId, token, isOpen, isMinimized, scrollToBottom, user?.user_id]);

  useEffect(() => {
    loadHistory();
    connectWs();
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, token]);

  useEffect(() => {
    if (isOpen && !isMinimized) {
      scrollToBottom();
      setUnreadCount(0);
    }
  }, [isOpen, isMinimized, messages.length, scrollToBottom]);

  const sendMessage = () => {
    const text = inputValue.trim();
    if (!text || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({ type: "message", message: text }));
    setInputValue("");
  };

  const sendTyping = () => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    wsRef.current.send(JSON.stringify({ type: "typing" }));
    typingTimeoutRef.current = setTimeout(() => {}, 3000);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } catch {
      return "";
    }
  };

  // Floating chat button
  if (!isOpen) {
    return (
      <Button
        onClick={() => { setIsOpen(true); setUnreadCount(0); }}
        className="fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full shadow-lg bg-primary hover:bg-primary/90 p-0"
        data-testid="chat-open-btn"
      >
        <MessageCircle className="h-6 w-6" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full h-5 w-5 flex items-center justify-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </Button>
    );
  }

  return (
    <div
      className={`fixed bottom-6 right-6 z-50 bg-card border border-border rounded-2xl shadow-2xl flex flex-col transition-all duration-300 ${
        isMinimized ? "w-80 h-14" : "w-96 h-[32rem]"
      }`}
      data-testid="chat-panel"
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3 border-b border-border rounded-t-2xl bg-primary/5 cursor-pointer"
        onClick={() => isMinimized && setIsMinimized(false)}
        data-testid="chat-header"
      >
        <div className="flex items-center gap-2">
          <MessageCircle className="h-4 w-4 text-primary" />
          <span className="font-heading font-semibold text-sm text-foreground">Event Chat</span>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Circle className="h-2 w-2 fill-emerald-500 text-emerald-500" />
            {onlineUsers.length} online
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost" size="icon" className="h-7 w-7"
            onClick={(e) => { e.stopPropagation(); setIsMinimized(!isMinimized); }}
            data-testid="chat-minimize-btn"
          >
            {isMinimized ? <Maximize2 className="h-3.5 w-3.5" /> : <Minimize2 className="h-3.5 w-3.5" />}
          </Button>
          <Button
            variant="ghost" size="icon" className="h-7 w-7"
            onClick={(e) => { e.stopPropagation(); setIsOpen(false); }}
            data-testid="chat-close-btn"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Online users bar */}
          {onlineUsers.length > 0 && (
            <div className="px-3 py-2 border-b border-border/50 flex items-center gap-1.5 overflow-x-auto" data-testid="online-users-bar">
              <Users className="h-3 w-3 text-muted-foreground shrink-0" />
              {onlineUsers.map((u) => (
                <span
                  key={u.user_id}
                  className={`text-[10px] px-1.5 py-0.5 rounded-full shrink-0 ${ROLE_COLORS[u.role] || "bg-muted text-muted-foreground"}`}
                >
                  {u.name?.split(" ")[0]}
                </span>
              ))}
            </div>
          )}

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3" data-testid="chat-messages">
            {messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                <MessageCircle className="h-8 w-8 mb-2 opacity-30" />
                <p className="text-sm">No messages yet</p>
                <p className="text-xs">Start a conversation</p>
              </div>
            ) : (
              messages.map((msg, i) => {
                const isMe = msg.sender_id === user?.user_id;
                return (
                  <div
                    key={msg.message_id || i}
                    className={`flex flex-col ${isMe ? "items-end" : "items-start"}`}
                    data-testid={`chat-msg-${msg.message_id || i}`}
                  >
                    {!isMe && (
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className="text-xs font-medium text-foreground">{msg.sender_name}</span>
                        <Badge className={`text-[9px] px-1 py-0 h-4 ${ROLE_COLORS[msg.sender_role] || ""}`}>
                          {ROLE_LABELS[msg.sender_role] || msg.sender_role}
                        </Badge>
                      </div>
                    )}
                    <div
                      className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm break-words ${
                        isMe
                          ? "bg-primary text-primary-foreground rounded-br-md"
                          : "bg-muted text-foreground rounded-bl-md"
                      }`}
                    >
                      {msg.message}
                    </div>
                    <span className="text-[10px] text-muted-foreground mt-0.5 px-1">
                      {formatTime(msg.created_at)}
                    </span>
                  </div>
                );
              })
            )}
            {typingUsers.length > 0 && (
              <div className="text-xs text-muted-foreground italic">
                {typingUsers.map((u) => u.sender_name).join(", ")} typing...
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="px-3 py-3 border-t border-border" data-testid="chat-input-area">
            <div className="flex items-center gap-2">
              <Input
                placeholder="Type a message..."
                value={inputValue}
                onChange={(e) => { setInputValue(e.target.value); sendTyping(); }}
                onKeyDown={handleKeyDown}
                className="flex-1 h-9 text-sm rounded-full"
                data-testid="chat-message-input"
              />
              <Button
                size="icon" className="h-9 w-9 rounded-full shrink-0"
                onClick={sendMessage}
                disabled={!inputValue.trim()}
                data-testid="chat-send-btn"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
